/**
 * Rhythmz — SceneStore
 * ────────────────────
 * Persists AI-generated scenes to localStorage so they survive reloads.
 * Scenes can be saved, loaded, demoed, and deleted.
 */

const STORAGE_KEY = 'rhythmz_saved_scenes';

export interface SavedScene {
  id: string;
  name: string;
  state: Record<string, unknown>;
  timestamp: number;
}

export class SceneStore {
  private scenes: SavedScene[] = [];

  constructor() {
    this.load();
  }

  /** Load all saved scenes from localStorage. */
  private load(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) this.scenes = JSON.parse(raw);
    } catch {
      this.scenes = [];
    }
  }

  /** Persist to localStorage. */
  private save(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.scenes));
    } catch {
      // localStorage may be full or unavailable
    }
  }

  /** Save a scene state. Returns the new scene's id. */
  saveScene(name: string, state: Record<string, unknown>): string {
    const id = `scene-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const scene: SavedScene = { id, name, state, timestamp: Date.now() };
    this.scenes.push(scene);
    this.save();
    return id;
  }

  /** Get all saved scenes (newest first). */
  list(): SavedScene[] {
    return [...this.scenes].sort((a, b) => b.timestamp - a.timestamp);
  }

  /** Get a specific scene by id. */
  get(id: string): SavedScene | null {
    return this.scenes.find((s) => s.id === id) ?? null;
  }

  /** Delete a scene by id. */
  delete(id: string): void {
    this.scenes = this.scenes.filter((s) => s.id !== id);
    this.save();
  }

  /** Get the count. */
  get count(): number {
    return this.scenes.length;
  }
}
